import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      let data = {};
      try { data = await res.json(); } catch (parseErr) {
        // Response body may have been intercepted by monitoring; fall back to status-based message.
        console.debug("Admin login response was not JSON:", parseErr);
      }
      if (!res.ok) {
        const detail = data.detail;
        const msg = typeof detail === "string"
          ? detail
          : (res.status === 401 ? "Invalid email or password" : `Login failed (${res.status})`);
        setError(msg);
        setLoading(false);
        return;
      }
      onLogin?.(data.admin);
      navigate("/admin");
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
            style={{ backgroundColor: "var(--mp-solid)" }}
          >
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <h1
            className="text-3xl font-medium mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Admin Sign-in
          </h1>
          <p
            className="text-sm"
            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
          >
            Mutha Paras & Co. — Inquiry Dashboard access
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-[var(--mp-line)] rounded-sm p-8 space-y-5"
          data-testid="admin-login-form"
        >
          <div>
            <label
              className="text-xs uppercase tracking-wider font-medium mb-2 block"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--mp-muted)" }} />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@muthaparas.com"
                required
                autoComplete="username"
                className="pl-10 rounded-sm border-[var(--mp-line)] py-5 text-sm"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="admin-login-email-input"
              />
            </div>
          </div>

          <div>
            <label
              className="text-xs uppercase tracking-wider font-medium mb-2 block"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--mp-muted)" }} />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                autoComplete="current-password"
                className="pl-10 rounded-sm border-[var(--mp-line)] py-5 text-sm"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="admin-login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="text-xs px-3 py-2 rounded-sm border"
              style={{ borderColor: "#fecaca", backgroundColor: "#fef2f2", color: "#b91c1c", fontFamily: "'Manrope', sans-serif" }}
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-5 text-sm rounded-sm text-white"
            style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="admin-login-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
              </span>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <p
          className="text-center text-xs mt-6"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
        >
          Authorised personnel only.
        </p>
      </div>
    </div>
  );
}
